import React, { useEffect, useState, useMemo } from "react";
import { api, formatDate, formatApiErrorDetail } from "../lib/api";
import { Plus, CaretLeft, CaretRight, Trash, PencilSimple, MapPin, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIPI = [
  { v: "audit", label: "Audit", color: "#0033A0" },
  { v: "visita_cliente", label: "Visita Cliente", color: "#008A3C" },
  { v: "formazione", label: "Formazione", color: "#E65C00" },
  { v: "riunione", label: "Riunione", color: "#7B2CBF" },
  { v: "scadenza", label: "Scadenza", color: "#D32F2F" },
  { v: "altro", label: "Altro", color: "#64748B" },
];

const EMPTY = {
  titolo: "", tipo: "audit", cliente_id: "", data_inizio: new Date().toISOString().slice(0, 16),
  data_fine: "", tutto_il_giorno: false, luogo: "", descrizione: "", assegnato_a: "", completato: false,
};

function tipoMeta(t) { return TIPI.find(x => x.v === t) || TIPI[5]; }

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfGrid(d) {
  const s = startOfMonth(d);
  const wd = (s.getDay() + 6) % 7; // Monday-first
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() - wd);
}
function sameDay(a, b) { return a.toDateString() === b.toDateString(); }
function fmtDateKey(d) { return d.toISOString().slice(0, 10); }

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const DAYS = ["L", "M", "M", "G", "V", "S", "D"];

export default function Calendario() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(new Date());

  async function load() {
    try {
      const [e, c] = await Promise.all([api.get("/eventi"), api.get("/clients")]);
      setItems(e.data); setClients(c.data);
      try { const u = await api.get("/users"); setUsers(u.data); } catch {}
    } catch (err) {
      toast.error("Errore nel caricamento");
    }
  }
  useEffect(() => { load(); }, []);

  function openNew(initialDate) {
    const base = initialDate || new Date();
    const iso = base.toISOString().slice(0, 16);
    setForm({ ...EMPTY, data_inizio: iso });
    setEditing("new");
  }
  function openEdit(ev) {
    setForm({
      ...EMPTY, ...ev,
      data_inizio: (ev.data_inizio || "").slice(0, 16),
      data_fine: (ev.data_fine || "").slice(0, 16),
    });
    setEditing(ev);
  }
  function close() { setEditing(null); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || null,
        assegnato_a: form.assegnato_a || null,
        data_inizio: new Date(form.data_inizio).toISOString(),
        data_fine: form.data_fine ? new Date(form.data_fine).toISOString() : null,
      };
      if (editing === "new") {
        await api.post("/eventi", payload);
        toast.success("Evento creato");
      } else {
        await api.put(`/eventi/${editing.id}`, payload);
        toast.success("Evento aggiornato");
      }
      close();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }

  async function toggleComplete(ev) {
    try {
      await api.put(`/eventi/${ev.id}`, { ...ev, completato: !ev.completato });
      await load();
    } catch {}
  }

  async function remove(ev) {
    if (!window.confirm(`Eliminare "${ev.titolo}"?`)) return;
    try {
      await api.delete(`/eventi/${ev.id}`);
      toast.success("Eliminato");
      await load();
    } catch (err) {
      toast.error("Errore");
    }
  }

  const gridDays = useMemo(() => {
    const start = startOfGrid(cursor);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  function eventsOn(day) {
    const key = fmtDateKey(day);
    return items.filter(e => (e.data_inizio || "").slice(0, 10) === key);
  }

  const todayEvents = useMemo(() => eventsOn(selectedDay)
    .sort((a, b) => (a.data_inizio || "").localeCompare(b.data_inizio || "")), [items, selectedDay]);

  return (
    <div className="p-4 md:p-12 max-w-[1500px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Pianificazione</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Calendario</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} eventi totali</p>
        </div>
        <button onClick={() => openNew(selectedDay)} data-testid="new-evento-btn" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start">
          <Plus size={14} weight="bold" /> Nuovo Evento
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar grid */}
        <div className="lg:col-span-2 swiss-card p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold tracking-tight">{MONTHS[cursor.getMonth()]} <span className="text-[#64748B] font-mono">{cursor.getFullYear()}</span></h2>
            <div className="flex items-center gap-1">
              <button onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Oggi</button>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]" data-testid="cal-prev"><CaretLeft size={14} weight="bold" /></button>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]" data-testid="cal-next"><CaretRight size={14} weight="bold" /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {DAYS.map((d, i) => (
              <div key={i} className="bg-[#F4F5F7] text-center py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">{d}</div>
            ))}
            {gridDays.map((d, idx) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = sameDay(d, new Date());
              const isSelected = sameDay(d, selectedDay);
              const evs = eventsOn(d);
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDay(d)}
                  onDoubleClick={() => openNew(d)}
                  className={`bg-white min-h-[60px] md:min-h-[80px] p-1 md:p-1.5 text-left transition-all relative group
                    ${!inMonth ? "opacity-40" : ""}
                    ${isSelected ? "ring-2 ring-[#0033A0] ring-inset" : ""}
                    ${isToday ? "bg-[#0033A0]/5" : ""}
                    hover:bg-[#F4F5F7]`}
                  data-testid={`cal-day-${fmtDateKey(d)}`}
                >
                  <div className={`text-xs font-bold ${isToday ? "text-[#0033A0]" : ""}`}>{d.getDate()}</div>
                  <div className="mt-1 space-y-0.5 hidden sm:block">
                    {evs.slice(0, 3).map((e) => {
                      const meta = tipoMeta(e.tipo);
                      return (
                        <div key={e.id} className={`text-[10px] truncate px-1 py-0.5 rounded-sm ${e.completato ? "line-through opacity-60" : ""}`} style={{ background: meta.color + "20", color: meta.color, borderLeft: `2px solid ${meta.color}` }}>
                          {e.titolo}
                        </div>
                      );
                    })}
                    {evs.length > 3 && <div className="text-[9px] text-[#64748B]">+{evs.length - 3}</div>}
                  </div>
                  {/* Mobile: dot indicators */}
                  {evs.length > 0 && (
                    <div className="sm:hidden flex flex-wrap gap-0.5 mt-1">
                      {evs.slice(0, 4).map((e) => (
                        <span key={e.id} className="w-1.5 h-1.5 rounded-full" style={{ background: tipoMeta(e.tipo).color }} />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {TIPI.map(t => (
              <span key={t.v} className="text-[10px] uppercase tracking-[0.15em] font-bold flex items-center gap-1.5 text-[#64748B]">
                <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Day details */}
        <div className="swiss-card p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">{selectedDay.toLocaleDateString("it-IT", { weekday: "long" })}</div>
          <h3 className="font-display text-2xl font-bold mb-1">{selectedDay.getDate()} {MONTHS[selectedDay.getMonth()]}</h3>
          <p className="text-xs text-[#64748B] mb-6">{todayEvents.length} {todayEvents.length === 1 ? "evento" : "eventi"}</p>

          {todayEvents.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-[#E2E8F0] rounded-sm">
              <p className="text-sm text-[#64748B] mb-3">Nessun evento</p>
              <button onClick={() => openNew(selectedDay)} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline">+ Aggiungi</button>
            </div>
          ) : (
            <div className="space-y-3">
              {todayEvents.map(ev => {
                const meta = tipoMeta(ev.tipo);
                const time = ev.tutto_il_giorno ? "Tutto il giorno" : new Date(ev.data_inizio).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={ev.id} className="border-l-4 pl-3 py-2" style={{ borderColor: meta.color }} data-testid={`evento-${ev.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className={`flex-1 ${ev.completato ? "opacity-60" : ""}`}>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: meta.color }}>{meta.label}</div>
                        <div className={`font-semibold mt-0.5 ${ev.completato ? "line-through" : ""}`}>{ev.titolo}</div>
                        <div className="text-xs text-[#64748B] mt-1">{time}</div>
                        {ev.cliente_nome && <div className="text-xs text-[#64748B] mt-0.5">Cliente: {ev.cliente_nome}</div>}
                        {ev.luogo && <div className="text-xs text-[#64748B] mt-0.5 flex items-center gap-1"><MapPin size={11} /> {ev.luogo}</div>}
                      </div>
                      <div className="flex gap-0.5">
                        <button onClick={() => toggleComplete(ev)} className={`p-1.5 rounded-sm ${ev.completato ? "text-[#008A3C]" : "text-[#64748B]"} hover:bg-[#F4F5F7]`} title="Completato" data-testid={`complete-evento-${ev.id}`}>
                          <Check size={14} weight="bold" />
                        </button>
                        <button onClick={() => openEdit(ev)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(ev)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]"><Trash size={14} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto thin-scroll" data-testid="evento-modal">
            <div className="p-6 border-b border-[#E2E8F0] sticky top-0 bg-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{editing === "new" ? "Nuovo" : "Modifica"}</div>
              <h2 className="font-display text-2xl font-bold">Evento Calendario</h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Titolo *" full>
                <input required value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} className="inp" data-testid="evento-titolo" />
              </Field>
              <Field label="Tipo *">
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="inp" data-testid="evento-tipo">
                  {TIPI.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Cliente (opzionale)">
                <select value={form.cliente_id || ""} onChange={(e) => setForm({ ...form, cliente_id: e.target.value })} className="inp">
                  <option value="">— Nessuno —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.ragione_sociale}</option>)}
                </select>
              </Field>
              <Field label="Inizio *">
                <input type="datetime-local" required value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} className="inp" data-testid="evento-data-inizio" />
              </Field>
              <Field label="Fine">
                <input type="datetime-local" value={form.data_fine || ""} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} className="inp" />
              </Field>
              <Field label="Luogo" full>
                <input value={form.luogo || ""} onChange={(e) => setForm({ ...form, luogo: e.target.value })} className="inp" placeholder="Es. Sede cliente, Via Roma 1" />
              </Field>
              {users.length > 0 && (
                <Field label="Assegnato a">
                  <select value={form.assegnato_a || ""} onChange={(e) => setForm({ ...form, assegnato_a: e.target.value })} className="inp">
                    <option value="">— Nessuno —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Field>
              )}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="tutto-giorno" checked={form.tutto_il_giorno} onChange={(e) => setForm({ ...form, tutto_il_giorno: e.target.checked })} className="accent-[#0033A0]" />
                <label htmlFor="tutto-giorno" className="text-sm">Tutto il giorno</label>
              </div>
              <Field label="Descrizione" full>
                <textarea rows={3} value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="inp" />
              </Field>
            </div>
            <div className="p-6 border-t border-[#E2E8F0] flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" disabled={saving} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="evento-save-btn">{saving ? "…" : "Salva"}</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp { width: 100%; background: #fff; border: 1px solid #E2E8F0; border-radius: 4px; padding: 8px 12px; font-size: 14px; outline: none; } .inp:focus { border-color: #0033A0; box-shadow: 0 0 0 1px #0033A0; }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
